import { useState } from "react";
import {
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTenantTranslation } from "hooks/formatters";
import {
  InventoryTargetBound,
  InventoryTargetCopyScope,
} from "./ruleParameters";

const scopeOptions = [
  { scope: "all", copy: "autoApplyAllPeriods", clear: "autoClearAllPeriods" },
  {
    scope: "deployable",
    copy: "autoApplyDeployablePeriods",
    clear: "autoClearDeployablePeriods",
  },
  {
    scope: "forecast",
    copy: "autoApplyForecastPeriods",
    clear: "autoClearForecastPeriods",
  },
] as const;

export const InventoryTargetScopeMenu = ({
  scopes,
  action,
  label,
  onSelect,
  ...availability
}: { label: string; scopes: InventoryTargetCopyScope[] } & (
  | { action: "copy"; onSelect: (scope: InventoryTargetCopyScope) => void }
  | {
      action: "clear";
      clearScopes: Record<InventoryTargetBound, InventoryTargetCopyScope[]>;
      onSelect: (
        scope: InventoryTargetCopyScope,
        bound: InventoryTargetBound
      ) => void;
    }
)) => {
  const { t } = useTenantTranslation();
  const availableOptions = scopeOptions.filter(({ scope }) =>
    scopes.includes(scope)
  );
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Tooltip title={label}>
        <IconButton
          size="small"
          aria-label={label}
          onClick={(event) => {
            event.stopPropagation();
            setAnchor(event.currentTarget);
          }}
        >
          {action === "copy" ? (
            <ContentCopyIcon fontSize="inherit" />
          ) : (
            <DeleteOutlineIcon fontSize="inherit" />
          )}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => {
          setAnchor(null);
        }}
      >
        {action === "copy"
          ? availableOptions.map((option) => (
              <MenuItem
                key={option.scope}
                onClick={(event) => {
                  event.stopPropagation();
                  onSelect(option.scope);
                  setAnchor(null);
                }}
              >
                {t(option.copy)}
              </MenuItem>
            ))
          : (["min_quantity", "max_quantity"] as const).flatMap((bound) => {
              const title = t(
                bound === "min_quantity" ? "targetMin" : "targetMax"
              );
              return [
                <ListSubheader key={bound}>{title}</ListSubheader>,
                ...availableOptions.map((option) => (
                  <MenuItem
                    key={`${bound}/${option.scope}`}
                    disabled={
                      !(
                        "clearScopes" in availability &&
                        availability.clearScopes[bound].includes(option.scope)
                      )
                    }
                    aria-label={`${title}: ${t(option.clear)}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onSelect(option.scope, bound);
                      setAnchor(null);
                    }}
                  >
                    {t(option.clear)}
                  </MenuItem>
                )),
              ];
            })}
      </Menu>
    </>
  );
};
